import React, {
  FunctionComponent,
  useState,
  useEffect,
  createElement,
} from 'react';
import {
  Button as RaButton,
  useTranslate,
  useNotify,
  useRecordContext,
  useRefresh,
  useResourceContext,
} from 'react-admin';
import { useForm, useField } from 'react-final-form';
import {
  IconButton as MuiIconButton,
  Table as MuiTable,
  TableBody as MuiTableBody,
  TableRow as MuiTableRow,
  TableCell as MuiTableCell,
  Typography as MuiTypography,
  makeStyles,
} from '@material-ui/core';
import { httpClient } from '../../providers/dataProvider';
import { ModalForm } from '../modals';
import { Input as ConfigInput } from '../../config/types/field';
import useConfig from '../../hooks/useConfig';
import { RelatedRecordProps } from './types';
import Field from '../Field';
import { getIconComponentFromName } from '../../helpers/icons';
import { formatCharges } from '../../helpers/relatedRecord';
import { getErrorMessage } from '../../helpers/error';
import ValidatorFactory, {
  OneParamValidator,
  TwoParamValidator,
} from '../../factories/ValidatorFactory';
import { InputValidator } from '../../config/types/field';
import { Validator as RaValidator } from 'react-admin';

const useStyles = makeStyles({
  delete: {
    marginLeft: 'auto',
    marginTop: '8px',
    display: 'flex',
    color: '#DC004E',
    backgroundColor: 'rgba(220, 0, 78, 0.04)',
    '&:hover': {
      backgroundColor: 'rgba(220, 0, 78, 0.15)',
    },
  },
  edit: {
    marginLeft: 'auto',
    marginTop: '8px',
    display: 'flex',
    color: '#777777',
    backgroundColor: '#E2E2E2',
  },
  table: {
    marginBottom: '20px',
  },
});

const RelatedRecord: FunctionComponent<RelatedRecordProps> = (props) => {
  const config = useConfig();
  const t = useTranslate();
  const form = useForm();
  const record = useRecordContext();
  const resource = useResourceContext();
  const classes = useStyles(config.theme);
  const notify = useNotify();

  const [showDialog, setShowDialog] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [items, setItems] = useState<any[]>([]);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<number | null>(null);
  const [indexToDelete, setIndexToDelete] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const refresh = useRefresh();
  const deleteIcon = getIconComponentFromName('Delete');
  const editIcon = getIconComponentFromName('Edit');

  const {
    recordToCreate,
    inputs: inputsItems,
    addRecordLabel,
    filter_resource: filterResource,
    filter_attribute: filterAttribute,
    modalTitle,
    readOnly = false,
    validators = [],
  } = props.relatedRecord;

  const isReadOnly =
    resource === 'invoices' && recordToCreate === 'charges' && record?.id
      ? record?.status !== 'draft'
      : readOnly;

  const validate = (validators || []).reduce(
    (acc: RaValidator[], validator: InputValidator): RaValidator[] => {
      const validatorFunction =
        validator.value !== undefined
          ? (ValidatorFactory.getValidator(validator.key) as TwoParamValidator)(
              validator.value,
              validator.message ??
                `resource.validators.${validator.key}.message`
            )
          : (ValidatorFactory.getValidator(validator.key) as OneParamValidator)(
              `resource.validators.${validator.key}.message`
            );

      return [...acc, validatorFunction];
    },
    []
  );

  const {
    input,
    meta: { touched, error },
  } = useField(recordToCreate, {
    validate: (value) => {
      for (const v of validate) {
        const err = v(value, form.getState().values, props);
        if (err) return err;
      }

      return undefined;
    },
  });

  const handleOpenModalClick = () => setShowDialog(true);
  const handleCloseModalClick = () => {
    setShowDialog(false);
    setEditMode(false);
    setSelectedItem(null);
  };

  const handleClick = async (values: Record<string, any>) => {
    const allItems = [...items];
    const itemToAdd =
      recordToCreate === 'payments'
        ? { invoice_id: record.id, ...values }
        : values;

    if (editMode && selectedItem !== null) {
      allItems[selectedItem] = { ...allItems[selectedItem], ...itemToAdd };
      if (recordToCreate === 'payments' && allItems[selectedItem].id) {
        try {
          await httpClient(
            `${config.apiUrl}/payments/${allItems[selectedItem].id}`,
            {
              method: 'PUT',
              body: JSON.stringify(allItems[selectedItem]),
            }
          );
          notify('notify.success.payment.updated', { type: 'success' });
          refresh();
        } catch (error) {
          notify('notify.error.payment.updated', {
            type: 'error',
          });
          refresh();
        }
      }

      if (recordToCreate === 'charges') {
        (window as any).__charges_modified__ = true;
      }
      setEditMode(false);
      setSelectedItem(null);
    } else {
      // Ajout
      allItems.push(itemToAdd);

      if (recordToCreate === 'payments') {
        try {
          await httpClient(`${config.apiUrl}/payments`, {
            method: 'POST',
            body: JSON.stringify(itemToAdd),
          });
          notify('notify.success.payment.create', { type: 'success' });
          refresh();
        } catch (error: any) {
          const errorMessage = getErrorMessage(error);
          notify(errorMessage, { type: 'error' });
          refresh();
        }
      }
    }

    if (recordToCreate === 'charges') {
      (window as any).__charges_modified__ = true;
    }
    setItems(allItems);
    setShowDialog(false);
  };

  const handleEdit = (index: number) => {
    setEditMode(true);
    setSelectedItem(index);
    handleOpenModalClick();
  };

  const handleDeleteClick = (index: number) => {
    if (recordToCreate === 'payments') {
      setIndexToDelete(index);
      setShowConfirmDialog(true);
    } else if (recordToCreate === 'charges' && items[index].charge_id) {
      const newItems = [...items];
      newItems[index] = { ...newItems[index], _destroy: true };
      (window as any).__charges_modified__ = true;
      setItems(newItems);
    } else {
      const newItems = [...items];
      if (recordToCreate === 'charges' && newItems[index].charge_id) {
        newItems[index] = { ...newItems[index], _destroy: true };
      } else {
        newItems.splice(index, 1);
      }
      if (recordToCreate === 'charges') {
        (window as any).__charges_modified__ = true;
      }
      setItems(newItems);
    }
  };

  const confirmDelete = async () => {
    if (indexToDelete !== null) {
      const itemToDelete = items[indexToDelete];
      if (recordToCreate === 'payments') {
        try {
          await httpClient(`${config.apiUrl}/payments/${itemToDelete.id}`, {
            method: 'DELETE',
          });
          notify('notify.success.payment.deleted', { type: 'success' });
          refresh();
        } catch (error) {
          notify('notify.error.payment.deleted', {
            type: 'error',
          });
          refresh();
          setShowConfirmDialog(false);
          setIndexToDelete(null);

          return;
        }
      }

      const newItems = [...items];
      newItems.splice(indexToDelete, 1);
      setItems(newItems);
    }

    setShowConfirmDialog(false);
    setIndexToDelete(null);
  };

  const cancelDelete = () => {
    setShowConfirmDialog(false);
    setIndexToDelete(null);
  };

  // Met à jour le formulaire parent
  useEffect(() => {
    input.onChange(items);
  }, [items]);

  const resourceInstance = config.getResource(recordToCreate);

  // inputs mapping
  const inputs: any[] = Object.entries(inputsItems).map(([key, input]) => ({
    ...resourceInstance.getAttribute(key),
    ...(input as ConfigInput),
    ...(editMode &&
      selectedItem !== null && {
        initialValue: items[selectedItem][key]
          ? input.type === 'date'
            ? new Date(items[selectedItem][key])
            : items[selectedItem][key]
          : null,
      }),
    source: key,
    ...(recordToCreate === 'payments' &&
      key === 'amount' && {
        type: 'number',
      }),
  }));

  const loadItems = () => {
    if (!filterAttribute || !record.id) return;

    setLoading(true);

    const filter: Record<string, string> = {
      [`${filterResource}_id`]: String(record.id),
    };

    // Encoder le JSON pour la query string
    const encodedFilter = encodeURIComponent(JSON.stringify(filter));

    httpClient(`${config.apiUrl}/${recordToCreate}?filter=${encodedFilter}`)
      .then(({ json }: any) => {
        const dataFormatted =
          recordToCreate === 'charges' ? formatCharges(json) : json;
        setItems(dataFormatted);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        notify('notify.error.history', 'error');
      });
  };

  useEffect(() => {
    if (filterResource && filterAttribute) loadItems();
  }, [record.id]);

  return (
    <>
      {!isReadOnly && (
        <RaButton label={t(addRecordLabel)} onClick={handleOpenModalClick} />
      )}

      <ModalForm
        open={showDialog}
        title={
          editMode && recordToCreate === 'payments'
            ? t('payments.modal.edit_title')
            : t(modalTitle)
        }
        handleClick={handleClick}
        handleClose={handleCloseModalClick}
        inputs={inputs}
        resource={recordToCreate}
      />
      <MuiTable aria-label="Related record table" className={classes.table}>
        <MuiTableBody>
          {items.map((item, index) => {
            if (item._destroy) return null;

            return (
              <MuiTableRow key={item.title || item.id}>
                {inputs.map((input) => {
                  if (input.hide) return null;
                  if (
                    recordToCreate === 'payments' &&
                    input.source === 'charge_title'
                  )
                    return null;
                  if (recordToCreate === 'charges' && input.source === 'amount')
                    return null;
                  const recordWithAmount =
                    input.source === 'amount' && item.amount != null
                      ? {
                          ...item,
                          amount: new Intl.NumberFormat('fr-FR', {
                            style: 'currency',
                            currency: 'EUR',
                          }).format(item.amount),
                        }
                      : item;

                  return (
                    <MuiTableCell key={input.source}>
                      <Field
                        record={recordWithAmount}
                        source={input.source}
                        type={input.type}
                        label={input.label}
                        addLabel
                        {...(input.choices && { choices: input.choices })}
                      />
                    </MuiTableCell>
                  );
                })}

                <MuiTableCell>
                  {!isReadOnly && (
                    <MuiIconButton
                      size="small"
                      onClick={() => handleEdit(index)}
                      className={classes.edit}
                      title={t('action.edit')}
                    >
                      {editIcon && createElement(editIcon)}
                    </MuiIconButton>
                  )}

                  {!isReadOnly &&
                    (recordToCreate === 'charges' ||
                      (recordToCreate === 'payments' && !item.payment_id)) && (
                      <MuiIconButton
                        size="small"
                        onClick={() => handleDeleteClick(index)}
                        className={classes.delete}
                      >
                        {deleteIcon && createElement(deleteIcon)}
                      </MuiIconButton>
                    )}
                </MuiTableCell>
              </MuiTableRow>
            );
          })}
        </MuiTableBody>
      </MuiTable>

      {recordToCreate === 'payments' && showConfirmDialog && (
        <ModalForm
          open={showConfirmDialog}
          title={t('resource.payment.delete.button.content')}
          handleClick={confirmDelete}
          handleClose={cancelDelete}
          inputs={[]}
          resource={recordToCreate}
        />
      )}
      {touched && error && (
        <MuiTypography variant="caption" color="error">
          {t(error)}
        </MuiTypography>
      )}
    </>
  );
};

export default RelatedRecord;
